/**
 * Copyright (c) 2025 Editoor Contributors
 *
 * Licensed under MIT License
 */

/**
 * Shell Menu Integration
 *
 * Allows apps to register their menus with the shell when running in iframe mode.
 * Apps should use this instead of showing their own menu bar when in shell.
 */

import type { SyncStatus } from './yjsSync';

export interface ShellMenuItem {
    id: string;
    label: string;
    type?: 'button' | 'separator' | 'link';
    href?: string;  // For link type
    disabled?: boolean;
    danger?: boolean;  // Red/danger styling
}

/**
 * Widget mode detection
 *
 * When plugins are imported by Drive to render as inline widgets,
 * they should not initialize their full-page mode (shell menu, undo/redo, etc).
 * Drive sets widget mode before importing, and plugins check it to skip initialization.
 *
 * Uses window global to ensure Drive and plugins see the same state
 * even when bundled separately.
 */
declare global {
    interface Window {
        __editoorWidgetMode?: boolean;
    }
}

/**
 * Set widget mode (called by Drive before importing widget modules)
 */
export function setWidgetMode(isWidget: boolean): void {
    window.__editoorWidgetMode = isWidget;
}

/**
 * Check if running in widget mode (embedded in Drive)
 * Plugins should use this to skip full-page initialization when true.
 */
export function isWidgetMode(): boolean {
    return window.__editoorWidgetMode === true;
}

/**
 * Register an app menu with the shell
 * @param name Menu title (e.g., "Drive", "Doc")
 * @param items Menu items to display
 */
export function registerShellMenu(name: string, items: ShellMenuItem[]): void {
    window.parent.postMessage({
        type: 'shell:registerMenu',
        payload: { name, items }
    }, window.location.origin);
}

/**
 * Listen for menu actions from the shell
 * @param callback Function called with action ID when menu item is clicked
 * @param isLocked When it returns true the page is locked (its document
 *   session is stale) and actions are dropped instead of delivered
 * @returns Cleanup function to remove the listener
 */
export function onShellMenuAction(callback: (actionId: string) => void, isLocked?: () => boolean): () => void {
    const handler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'shell:menuAction') {
            if (isLocked?.()) return;
            callback(event.data.payload.actionId);
        }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
}

/**
 * Helper to create a separator menu item
 */
export function menuSeparator(): ShellMenuItem {
    return { id: '', label: '', type: 'separator' };
}

/**
 * Helper to create a button menu item
 */
export function menuButton(id: string, label: string, options?: { disabled?: boolean; danger?: boolean }): ShellMenuItem {
    return { id, label, type: 'button', ...options };
}

/**
 * Helper to create a link menu item
 */
export function menuLink(id: string, label: string, href: string): ShellMenuItem {
    return { id, label, type: 'link', href };
}

/**
 * The open document's sync state, as the shell's connection indicator shows
 * it (see reportSyncStatusToShell in shellSyncStatus.ts).
 */
export interface ShellSyncState {
    status: SyncStatus;
    /** Unsent edits: the indicator turns orange while any wait. */
    pendingCount: number;
    /** The current error or the session lock (the indicator turns red and shows it). */
    error?: string;
}

/**
 * Report app status to the shell
 * @param status Object with readOnly, language, supportedLanguages and/or sync
 */
export function setShellStatus(status: {
    readOnly?: boolean;
    language?: string;
    supportedLanguages?: string[];
    sync?: ShellSyncState;
}): void {
    window.parent.postMessage({
        type: 'shell:setStatus',
        payload: status
    }, window.location.origin);
}

/**
 * Listen for settings changes from the shell (dark mode, language)
 * @param callback Function called with settings when they change
 * @returns Cleanup function to remove the listener
 */
export function onShellSettings(callback: (settings: { darkMode: boolean; language: string }) => void): () => void {
    const handler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'shell:settings') {
            callback(event.data.payload);
        }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
}

/**
 * Request current settings from the shell
 * Use this on app initialization to get the current dark mode and language settings
 */
export function requestShellSettings(): void {
    window.parent.postMessage({
        type: 'shell:requestSettings'
    }, window.location.origin);
}

/**
 * Report connection status to the shell
 * @param status Object with count, users array, and connected boolean
 */
export function setShellConnectionStatus(status: {
    count: number;
    users: string[];
    connected: boolean;
}): void {
    window.parent.postMessage({
        type: 'shell:setConnectionStatus',
        payload: status
    }, window.location.origin);
}

/**
 * Report undo/redo state to the shell
 * Call this whenever undo/redo availability changes
 * @param state Object with canUndo and canRedo booleans
 */
export function setShellUndoRedoState(state: {
    canUndo: boolean;
    canRedo: boolean;
}): void {
    window.parent.postMessage({
        type: 'shell:setUndoRedoState',
        payload: state
    }, window.location.origin);
}

/**
 * Ask the shell to hide (or restore) its header so the app fills the screen.
 * In a plain browser the shell also enters real fullscreen. The shell restores
 * its header by itself when the user navigates away from the document.
 * @param immersive true to hide the shell header, false to restore it
 */
export function setShellImmersive(immersive: boolean): void {
    window.parent.postMessage({
        type: 'shell:setImmersive',
        payload: { immersive }
    }, window.location.origin);
}

/**
 * Listen for undo/redo commands from the shell
 * @param onUndo Function to call when undo is requested
 * @param onRedo Function to call when redo is requested
 * @returns Cleanup function to remove the listener
 */
export function onShellUndoRedo(onUndo: () => void, onRedo: () => void): () => void {
    const handler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'shell:undo') {
            onUndo();
        } else if (event.data?.type === 'shell:redo') {
            onRedo();
        }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
}
